/**
 * Layer Controls & Navigation Toolbar
 * Enables dynamic switching of surface textures (Albedo, LOLA Topography, Shaded Relief),
 * Selenographic grids, lighting models, camera orientation, and landmark quick-jump.
 */

import { LUNAR_FEATURES } from '../data/lunarFeatures.js';

export class LayerControls {
  constructor(container, callbacks = {}) {
    this.container = container;
    this.callbacks = callbacks;

    this.element = document.createElement('div');
    this.element.className = 'controls-bar glass-panel';
    this.container.appendChild(this.element);

    this.render();
  }

  render() {
    this.element.innerHTML = `
      <div class="control-group">
        <span class="group-title">SURFACE LAYER</span>
        <div class="button-row">
          <button class="ctrl-btn active" data-layer="0" id="btn-layer-albedo" title="True Photographic Albedo (NASA LROC WAC)">
            📸 Natural Albedo
          </button>
          <button class="ctrl-btn" data-layer="1" id="btn-layer-lola" title="LOLA Topographic Elevation False-Color">
            🌈 Topography (LOLA)
          </button>
          <button class="ctrl-btn" data-layer="2" id="btn-layer-relief" title="Slope & Shaded Relief Morphology">
            ⛰️ Shaded Relief
          </button>
        </div>
      </div>

      <div class="control-group">
        <span class="group-title">OVERLAYS & LIGHTING</span>
        <div class="button-row">
          <button class="ctrl-btn" id="btn-toggle-grid" title="Toggle Selenographic Parallels & Meridians">
            🌐 Grid
          </button>
          <button class="ctrl-btn active" id="btn-toggle-markers" title="Toggle Surface Mission & Crater Pins">
            📍 Landmarks
          </button>
          <button class="ctrl-btn" id="btn-toggle-light" title="Toggle Sun Directional Lighting vs Uniform Studio Lighting">
            ☀️ Sun Phase
          </button>
          <button class="ctrl-btn" id="btn-toggle-rotate" title="Toggle Automatic Lunar Axial Rotation">
            ⚡ Auto-Spin
          </button>
        </div>
      </div>

      <div class="control-group">
        <span class="group-title">QUICK ORIENTATION</span>
        <div class="button-row">
          <button class="ctrl-btn quick-nav" id="btn-nav-near" title="Face Earth-facing Nearside (0° Lon, 0° Lat)">
            Nearside
          </button>
          <button class="ctrl-btn quick-nav" id="btn-nav-far" title="Face Hidden Farside (180° Lon)">
            Farside
          </button>
          <button class="ctrl-btn quick-nav" id="btn-nav-south" title="View South Pole Aitken & Artemis Target">
            South Pole
          </button>
          <button class="ctrl-btn quick-nav" id="btn-nav-north" title="View North Pole Peary Crater">
            North Pole
          </button>
        </div>
      </div>

      <div class="control-group">
        <span class="group-title">LANDMARK JUMP</span>
        <select class="landmark-select" id="landmark-select">
          <option value="" disabled selected>Select Feature to Fly To...</option>
          <optgroup label="Apollo Missions">
            ${LUNAR_FEATURES.filter(f => f.type === 'apollo').map(f => `<option value="${f.id}">${f.name}</option>`).join('')}
          </optgroup>
          <optgroup label="Robotic Missions">
            ${LUNAR_FEATURES.filter(f => f.type === 'robotic').map(f => `<option value="${f.id}">${f.name}</option>`).join('')}
          </optgroup>
          <optgroup label="Impact Craters">
            ${LUNAR_FEATURES.filter(f => f.type === 'crater').map(f => `<option value="${f.id}">${f.name}</option>`).join('')}
          </optgroup>
          <optgroup label="Maria & Basins">
            ${LUNAR_FEATURES.filter(f => f.type === 'mare').map(f => `<option value="${f.id}">${f.name}</option>`).join('')}
          </optgroup>
          <optgroup label="Farside Features">
            ${LUNAR_FEATURES.filter(f => f.type === 'farside').map(f => `<option value="${f.id}">${f.name}</option>`).join('')}
          </optgroup>
        </select>
      </div>

      <!-- LOLA Legend (hidden unless LOLA Topography layer is active) -->
      <div class="lola-legend hidden" id="lola-legend">
        <span class="legend-title">LOLA ELEVATION (KM)</span>
        <div class="legend-bar"></div>
        <div class="legend-labels">
          <span>-9.1 km (SPA)</span>
          <span>-3 km</span>
          <span>0 km</span>
          <span>+4 km</span>
          <span>+10.7 km (Dirichlet)</span>
        </div>
      </div>
    `;

    this.initEvents();
  }

  initEvents() {
    // 1. Layer switcher
    const layerButtons = this.element.querySelectorAll('[data-layer]');
    const lolaLegend = this.element.querySelector('#lola-legend');

    layerButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        layerButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const mode = parseInt(btn.dataset.layer, 10);
        if (mode === 1) {
          lolaLegend.classList.remove('hidden');
        } else {
          lolaLegend.classList.add('hidden');
        }

        if (this.callbacks.onLayerChange) this.callbacks.onLayerChange(mode);
      });
    });

    // 2. Overlays & Lighting
    const gridBtn = this.element.querySelector('#btn-toggle-grid');
    gridBtn.addEventListener('click', () => {
      gridBtn.classList.toggle('active');
      if (this.callbacks.onToggleGrid) this.callbacks.onToggleGrid(gridBtn.classList.contains('active'));
    });

    const markersBtn = this.element.querySelector('#btn-toggle-markers');
    markersBtn.addEventListener('click', () => {
      markersBtn.classList.toggle('active');
      if (this.callbacks.onToggleMarkers) this.callbacks.onToggleMarkers(markersBtn.classList.contains('active'));
    });

    const lightBtn = this.element.querySelector('#btn-toggle-light');
    lightBtn.addEventListener('click', () => {
      lightBtn.classList.toggle('active');
      const isStudio = !lightBtn.classList.contains('active');
      lightBtn.textContent = isStudio ? '💡 Studio Light' : '☀️ Sun Phase';
      if (this.callbacks.onToggleLight) this.callbacks.onToggleLight(isStudio);
    });

    const rotateBtn = this.element.querySelector('#btn-toggle-rotate');
    rotateBtn.addEventListener('click', () => {
      rotateBtn.classList.toggle('active');
      if (this.callbacks.onToggleRotate) this.callbacks.onToggleRotate(rotateBtn.classList.contains('active'));
    });

    // 3. Quick Nav
    this.element.querySelector('#btn-nav-near').addEventListener('click', () => {
      if (this.callbacks.onNavNearside) this.callbacks.onNavNearside();
    });
    this.element.querySelector('#btn-nav-far').addEventListener('click', () => {
      if (this.callbacks.onNavFarside) this.callbacks.onNavFarside();
    });
    this.element.querySelector('#btn-nav-south').addEventListener('click', () => {
      if (this.callbacks.onNavSouthPole) this.callbacks.onNavSouthPole();
    });
    this.element.querySelector('#btn-nav-north').addEventListener('click', () => {
      if (this.callbacks.onNavNorthPole) this.callbacks.onNavNorthPole();
    });

    // 4. Landmark Select
    const select = this.element.querySelector('#landmark-select');
    select.addEventListener('change', (e) => {
      const featureId = e.target.value;
      const feature = LUNAR_FEATURES.find(f => f.id === featureId);
      if (feature && this.callbacks.onSelectLandmark) {
        this.callbacks.onSelectLandmark(feature);
      }
    });
  }
}
